import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Boxes,
  Truck,
  MapPin,
  ArrowRight,
  Tag,
  Layers,
  Shirt,
  BookOpen,
  Sparkles,
  Feather,
  Scale,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à Andelu (style éditorial) ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Service de livraison de cartons de déménagement à domicile
              {" "}{city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce qu&apos;Andelu bénéficie d&apos;un cadre de vie
              paisible mais parfois éloigné des grandes zones
              commerciales, nous simplifions vos préparatifs en
              apportant le matériel directement chez vous. Nous
              assurons une livraison rapide et soignée sur
              l&apos;ensemble du territoire de la commune, ainsi que
              dans les localités voisines comme Maule, Thoiry ou
              Bazemont.
            </p>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              En commandant vos fournitures en ligne, vous planifiez
              la réception de vos cartons à plat selon vos
              disponibilités, ce qui vous évite des déplacements
              fastidieux et vous fait gagner un temps précieux avant
              le jour J.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            <div className="bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <MapPin className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  Zones desservies
                </h3>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {["Andelu", "Maule", "Thoiry", "Bazemont"].map((n) => (
                  <span
                    key={n}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#EEF3F7] border border-[#cfdbe6] text-[12.5px] font-semibold text-[#0f1d2b]"
                  >
                    <MapPin className="h-3 w-3 text-[#237cb5]" strokeWidth={2.6} />
                    {n}
                  </span>
                ))}
              </div>
            </div>

            <div className="bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Truck className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                    Livraison à plat planifiée
                  </h3>
                  <p className="mt-1 text-[13.5px] text-[#3a4856] leading-relaxed">
                    Réception au créneau de votre choix, fournitures
                    propres et sèches à l&apos;arrivée.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Emballages robustes & packs adaptés ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="overline" style={{ color: "#237cb5" }}>
              Emballages &amp; packs
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b] leading-tight">
              Des emballages robustes et des packs adaptés à vos
              volumes
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Notre gamme a été spécialement conçue pour répondre aux
              exigences de chaque type de logement. Nous proposons
              des cartons standards double cannelure très résistants
              pour la vaisselle et les objets du quotidien, mais aussi
              des formats spécifiques comme les cartons penderie
              équipés d&apos;une tringle pour suspendre vos vêtements
              sur cintres, ou des cartons livres plus compacts.
            </p>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Pour vous faciliter la tâche, vous pouvez opter pour nos
              packs clés en main configurés selon la taille de votre
              habitation, ou composer vous-même votre panier avec nos
              rouleaux de film à bulles et nos adhésifs de qualité
              supérieure.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/kits-cartons"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
                data-testid="city-andelu-cta-kits"
              >
                <Boxes className="h-4 w-4" strokeWidth={2.6} />
                Voir les packs clés en main
                <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
              </Link>
              <Link
                to="/cartons-demenagement"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
                data-testid="city-andelu-cta-cartons"
              >
                <Package className="h-4 w-4" strokeWidth={2.6} />
                Composer mon panier
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              {
                title: "Double cannelure",
                body: "Vaisselle, objets du quotidien — résistance maximale à l'écrasement.",
                Icon: Layers,
              },
              {
                title: "Carton penderie",
                body: "Tringle métallique intégrée pour vêtements sur cintres, zéro pli.",
                Icon: Shirt,
              },
              {
                title: "Carton livres",
                body: "Format compact pour transporter vos bibliothèques sans casser le dos.",
                Icon: BookOpen,
              },
              {
                title: "Film bulle & adhésifs",
                body: "Protection des objets fragiles et fermeture professionnelle de vos boîtes.",
                Icon: Tag,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5"
              >
                <span className="flex items-center justify-center h-10 w-10 rounded-xl bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="mt-4 font-display font-extrabold text-[16px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[13.5px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Clés d'un emballage réussi ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Méthode d&apos;emballage
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Les clés d&apos;un emballage réussi pour votre transport
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour que le chargement et le transport se déroulent sans
            encombre, quelques règles simples d&apos;organisation font
            toute la différence. Nous vous conseillons de répartir
            soigneusement le poids en plaçant les objets les plus
            lourds au fond des cartons et en comblant les espaces
            vides avec du film bulle pour empêcher tout mouvement.
          </p>
          <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
            Prenez également le temps de marquer chaque carton à
            l&apos;aide d&apos;un feutre en indiquant sa pièce de
            destination et la mention &laquo;&nbsp;Fragile&nbsp;&raquo;
            si nécessaire, un réflexe qui fera gagner un temps
            précieux au moment du déchargement dans votre nouvelle
            habitation {city.aCity}.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            {
              title: "Répartir le poids",
              body: "Objets lourds au fond, légers au-dessus pour équilibrer la boîte.",
              Icon: Scale,
            },
            {
              title: "Combler les vides",
              body: "Du film bulle dans chaque interstice pour empêcher les mouvements.",
              Icon: Feather,
            },
            {
              title: "Marquer chaque carton",
              body: "Pièce de destination + mention « Fragile » au feutre sur les côtés.",
              Icon: Tag,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-andelu-cta-protection"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Voir les protections
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-andelu-cta-feutres"
          >
            <Sparkles className="h-4 w-4" strokeWidth={2.6} />
            Marqueurs &amp; adhésifs
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-andelu-cta-back-dept"
        >
          <ShieldCheck className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
